import { useState, type ReactElement, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { NavDrawer } from '@citibox-panel/ui';
import { can } from '@/lib/access';
import { ARRIVAL_STATE } from '@/lib/arrival';
import { useSession } from '@/lib/session';
import { normalizePath, type ViewHome } from '@/lib/spaceNavigation';
import { SPACES } from '@/lib/spaces';

export interface NavEntry extends ViewHome {
  label: string;
  icon: ReactNode;
}

// Per browser, not per person: a layout preference with nothing sensitive in it.
export const PRODUCT_NAV_COLLAPSED_KEY = 'citibox-panel:producto-nav-collapsed';

// TODO: i18n
const COPY = { collapse: 'Contraer menú', expand: 'Expandir menú' } as const;

const PRODUCT = SPACES.find((space) => space.id === 'product');

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(PRODUCT_NAV_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(PRODUCT_NAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Blocked storage: the choice lasts until the next load.
  }
}

export interface ProductNavProps {
  entries: readonly NavEntry[];
}

export function ProductNav({ entries }: ProductNavProps): ReactElement | null {
  const { roleIds, permissions } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Read in the initializer so a collapsed menu never paints expanded first.
  const [collapsed, setCollapsed] = useState(readCollapsed);

  // The role organises, the permission filters.
  if (PRODUCT === undefined || !roleIds.includes(PRODUCT.roleId)) return null;
  const path = normalizePath(pathname);
  const items = entries
    .filter((entry) => can(permissions, entry.permission))
    .map((entry) => ({ href: entry.to, label: entry.label, icon: entry.icon, current: entry.to === path }));
  if (items.length === 0) return null;

  return (
    <NavDrawer
      aria-label={PRODUCT.label}
      items={items}
      onNavigate={(href) => {
        // Re-picking the current screen must not push a duplicate entry for «Atrás».
        if (href === path) return;
        void navigate(href, { state: ARRIVAL_STATE });
      }}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        writeCollapsed(next);
      }}
      collapseLabel={COPY.collapse}
      expandLabel={COPY.expand}
    />
  );
}
